import type { components } from '@connected/api-client/api.generated';

import { useAuthentication } from '@connected/api-client/hooks/use-authentication';
import { portaalQueryOptions } from '@connected/portaal-sdk/queries/options';
import { Carousel, CarouselContent, CarouselItem } from '@connected/ui/components/carousel';
import { Image } from '@connected/ui/components/image';
import { useQuery } from '@tanstack/react-query';
import { createFileRoute, useNavigate } from '@tanstack/react-router';
import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';

import { Breadcrumb } from '@/components/breadcrumb';
import { CheckmarkCircle } from '@/components/checkmark-circle';
import { DataTable } from '@/components/data-table';
import { LoadingPage } from '@/components/loading-page';

export const Route = createFileRoute(
    '/_public/customer/locations/$locationId/electrical/projects/$projectId/_overview/pv-installations/$pvInverterId/',
)({
    component: RouteComponent,
});

type ListObservationItems = components['schemas']['ListElectricalProjectObservationPortalQueryResultObservation'];

type PVInventerStringItems = components['schemas']['ViewElectricalProjectPVInverterPortalQueryResultString'];

type ListNameValue = {
    Name: string | null;
    Value: string | Date | number | null | boolean;
};

type ListSeverityCode = components['schemas']['ListElectricalProjectObservationQueryResultObservation']['Code'] & {
    Value: string | null;
};

function RouteComponent() {
    const auth = useAuthentication();
    const { t } = useTranslation('customer-module-electrical');
    const params = Route.useParams();
    const navigate = useNavigate();

    const projectQuery = useQuery(portaalQueryOptions.electrical.pvInverter.projectView({
        ElectricalProjectPVInverterId: params.pvInverterId,
    }));

    const observations = useQuery(portaalQueryOptions.electrical.observations.listPV({
        ElectricalProjectId: params.projectId ?? null,
        ObjectKind: 'PVInverter',
        ElectricalProjectPVInverterId: params.pvInverterId,
    }));

    const objectQuery = useQuery({
        ...portaalQueryOptions.portal.objects.view({ ObjectId: params.locationId }),
        enabled: !!auth.isAuthenticated && !!params.locationId,
    });

    const projectViewQuery = useQuery({
        ...portaalQueryOptions.electrical.project.view({ ElectricalProjectId: params.projectId }),
        enabled: !auth.isAuthenticated && !!params.projectId,
    });

    const colorCodes = useQuery(portaalQueryOptions.electrical.distributionBoards.list({
        ElectricalProjectId: params.projectId ?? null,
        Kinds: ['ControlBox'],
    }));

    const colors = colorCodes?.data?.Codes?.map(row => ({
        kind: row.Kind,
        hex: row.HexColorCode,
    }));

    const severity = useMemo<ListSeverityCode[]>(() => [{
        Kind: 'Severe',
        Value: t('module-electrical.overview.observations-table.columns.severe'),
    }, {
        Kind: 'Serious',
        Value: t('module-electrical.overview.observations-table.columns.serious'),
    }, {
        Kind: 'Minor',
        Value: t('module-electrical.overview.observations-table.columns.minor'),
    }, {
        Kind: 'Remark',
        Value: t('module-electrical.overview.observations-table.columns.remarks'),
    }], [t]);

    const nameValueColumns = useMemo(() => [
        {
            accessorKey: 'Name',
            header: () => (
                <DataTable.Header>
                    {t('module-electrical.distribution-boards.list.name')}
                </DataTable.Header>
            ),
            cell: ({ row }: { row: { original: ListNameValue } }) => (
                <DataTable.Cell variant="medium">
                    <div className="md:min-w-48 md:max-w-48 2xl:min-w-96 2xl:max-w-96 flex items-center min-h-8">
                        {row?.original?.Name}
                    </div>
                </DataTable.Cell>
            ),
        },
        {
            accessorKey: 'Value',
            header: () => (
                <DataTable.Header>
                    {t('module-electrical.distribution-boards.distribution-board.measurements.currents-table.header.value')}
                </DataTable.Header>
            ),
            cell: ({ row }: { row: { original: ListNameValue } }) => (
                <DataTable.Cell variant="medium">
                    <div className="md:min-w-48 md:max-w-48 2xl:min-w-96 2xl:max-w-96 flex items-center min-h-8">
                        {row?.original?.Value?.toString()}
                    </div>

                </DataTable.Cell>
            ),
        },
    ], [t]);

    const notificationColumns = useMemo(() => [
        {
            accessorKey: 'Category',
            meta: {
                sticky: true,
                width: 10,
            },
            header: () => (
                <DataTable.Header>
                    {t('module-electrical.overview.observations-table.columns.groupKind')}
                </DataTable.Header>
            ),
            cell: ({ row }: { row: { original: ListObservationItems } }) => (
                <DataTable.Cell variant="medium">
                    <div
                        onClick={
                            () => {
                                navigate({
                                    to: '/customer/locations/$locationId/electrical/projects/$projectId/pv-installations/$pvInverterId/observations/$observationId',
                                    params: {
                                        ...params,
                                        observationId: row.original?.ElectricalProjectObservationId ?? '',
                                    },
                                });
                            }
                        }
                        className="text-sm font-bold text-primary underline hover:cursor-pointer"
                    >
                        {severity.find(s => s.Kind === row?.original?.CodeKind)?.Value || t('module-electrical.report.code-kinds.undefined')}
                    </div>
                </DataTable.Cell>
            ),
        },
        {
            accessorKey: 'HexColorCode',
            header: () => (
                <DataTable.Header>
                    {t('module-electrical.pv-installations.table.severity')}
                </DataTable.Header>
            ),
            cell: ({ row }: { row: { original: ListObservationItems } }) => (
                <div className="flex items-center min-h-8">
                    <DataTable.Cell
                        variant="observation"
                        props={{
                            label: t('module-electrical.pv-installations.table.severity'),
                            color: colors?.find(c => c.kind === row?.original?.CodeKind)?.hex ?? '#FFFFFF',
                            children: null,
                        }}
                        label={t('module-electrical.distribution-boards.list.serious')}
                    />
                </div>
            ),
        },
        {
            accessorKey: 'Status',
            header: () => (
                <DataTable.Header>
                    {t('module-electrical.overview.attached-files-table.columns.status')}
                </DataTable.Header>
            ),
            cell: ({ row }: { row: { original: ListObservationItems } }) => (
                <div className="flex items-center min-h-8">
                    <CheckmarkCircle checked={!!row?.original?.IsFixed} />
                </div>
            ),
        },
        {
            accessorKey: 'LocationRemark',
            header: () => (
                <DataTable.Header>
                    {t('module-electrical.installation.location-description.title')}
                </DataTable.Header>
            ),
            cell: ({ row }: { row: { original: ListObservationItems } }) => (
                <DataTable.Cell variant="medium">
                    <div className="flex items-center min-h-8">
                        {row?.original?.LocationRemark}
                    </div>
                </DataTable.Cell>
            ),
        },
        {
            accessorKey: 'Remark',
            header: () => (
                <DataTable.Header>
                    {t('module-electrical.installation.notifications-table.header.comment')}
                </DataTable.Header>
            ),
            cell: ({ row }: { row: { original: ListObservationItems } }) => (
                <DataTable.Cell variant="medium">
                    {row?.original?.Remark && row.original.Remark.length > 40
                        ? `${row.original.Remark.slice(0, 40)}...`
                        : row.original.Remark}
                </DataTable.Cell>
            ),
        },
        {
            accessorKey: 'Date',
            header: () => (
                <DataTable.Header>
                    {t('module-electrical.overview.attached-files-table.columns.date')}
                </DataTable.Header>
            ),
            cell: ({ row }: { row: { original: ListObservationItems } }) => (
                <DataTable.Cell variant="medium">
                    <div className="flex items-center min-h-8">
                        {row?.original?.Date ? row.original.Date.split('T')[0] : ''}
                    </div>
                </DataTable.Cell>
            ),
        },
    ], [colors, navigate, params, severity, t]);

    const stringMeasurementColumns = useMemo(() => [
        {
            accessorKey: 'Identifier',
            header: () => (
                <DataTable.Header>
                    {t('module-electrical.distribution-boards.list.name')}
                </DataTable.Header>
            ),
            cell: ({ row }: { row: { original: PVInventerStringItems } }) => (
                <DataTable.Cell variant="medium">
                    <div className="flex items-center min-h-8">
                        {row?.original?.Identifier}
                    </div>
                </DataTable.Cell>
            ),
        },
        {
            accessorKey: 'Voltage',
            header: () => (
                <DataTable.Header>
                    {t('module-electrical.distribution-boards.distribution-board.measurements.voltages-table.title')}
                </DataTable.Header>
            ),
            cell: ({ row }: { row: { original: PVInventerStringItems } }) => (
                <DataTable.Cell variant="medium">
                    {row?.original?.Voltage}
                </DataTable.Cell>
            ),
        },
        {
            accessorKey: 'Resistance',
            header: () => (
                <DataTable.Header>
                    {t('module-electrical.pv-installations.table.resistance')}
                </DataTable.Header>
            ),
            cell: ({ row }: { row: { original: PVInventerStringItems } }) => (
                <DataTable.Cell variant="medium">
                    {row?.original?.Resistance}
                </DataTable.Cell>
            ),
        },
        {
            accessorKey: 'Current',
            header: () => (
                <DataTable.Header>
                    {t('module-electrical.pv-installations.table.current')}
                </DataTable.Header>
            ),
            cell: ({ row }: { row: { original: PVInventerStringItems } }) => (
                <DataTable.Cell variant="medium">
                    {row?.original?.Current}
                </DataTable.Cell>
            ),
        },
    ], [t]);

    if (projectQuery.isLoading || observations.isLoading || colorCodes.isLoading) {
        return <LoadingPage />;
    }

    return (
        <div className="max-w-screen-2xl flex flex-col flex-1 gap-5 pb-0 overflow-y-auto h-full">
            <div>
                <Breadcrumb
                    items={[
                        {
                            name: auth.isAuthenticated
                                ? objectQuery.data?.Object?.Name || t('module-electrical.projects.title')
                                : projectViewQuery.data?.Project?.ProjectObject?.Name || t('module-electrical.projects.title'),
                            to: '/customer/locations/$locationId',
                            params: { locationId: params.locationId },
                        },
                        {
                            name: t('module-electrical.name'),
                            to: '/customer/locations/$locationId/electrical/projects/$projectId/overview',
                            params: { ...params },
                        },
                        {
                            name: t('module-electrical.pv-installations.title'),
                            to: '/customer/locations/$locationId/electrical/projects/$projectId/pv-installations',
                            params: { ...params },
                        },
                        {
                            isActive: true,
                            name: projectQuery.data?.PVInverter?.Identifier ?? '',
                        },
                    ]}
                />
                <div className="font-semibold text-xl">
                    {projectQuery?.data?.PVInverter?.Identifier}
                </div>
            </div>
            <div className="w-full h-px block bg-border-primary"></div>

            <div className="grid grid-cols-1 gap-0 sm:grid-cols-2 sm:gap-2 md:grid-cols-3 md:gap-4">
                <div className="col-span-1 text-sm mb-4 md:mb-0">
                    <div className="font-semibold ">
                        {t('module-electrical.pv-installations.notificationOverview.title')}
                    </div>
                </div>
                <div className="col-span-2">
                    {observations?.data?.Observations && observations?.data?.Observations.length > 0
                        ? (
                                <DataTable
                                    columns={notificationColumns}
                                    data={observations?.data?.Observations ?? []}
                                    pageSize={5}
                                />
                            )
                        : (
                                <div className="flex items-center justify-center h-52 text-text-primary/50 rounded-lg border-2 border-dashed">
                                    <p>{t('module-electrical.pv-installations.messages.noObservationsAvailable')}</p>
                                </div>
                            )}
                </div>
            </div>

            <div className="w-full h-px block bg-border-primary"></div>

            <div className="grid grid-cols-1 gap-0 sm:grid-cols-2 sm:gap-2 md:grid-cols-3 md:gap-4">
                <div className="col-span-1 text-sm mb-4 md:mb-0">
                    <div className="font-semibold ">
                        {`${projectQuery?.data?.PVInverter?.Identifier} ${t('module-electrical.distribution-boards.distribution-board.images.title').toLocaleLowerCase()}`}
                    </div>
                </div>
                <div className="col-span-2">
                    {projectQuery?.data?.PVInverter?.Files && projectQuery?.data?.PVInverter?.Files.filter(row => row.Kind === 'PVInverterImage').length > 0
                        ? (
                                <Carousel loop>
                                    <CarouselContent className="-ml-2 md:-ml-4">
                                        {projectQuery?.data?.PVInverter?.Files.filter(row => row.Kind === 'PVInverterImage').map((file, idx) => (
                                            <CarouselItem className="pl-2 md:pl-4 basis-1/1 sm:basis-1/2 lg:basis-1/3 xl:basis-1/4" key={file.ElectricalProjectFileId ?? idx}>
                                                {file.File && (
                                                    <Image
                                                        previewer
                                                        className="h-52 w-auto object-contain rounded-lg"
                                                        alt={file?.File?.Name ?? ''}
                                                        queryOptions={portaalQueryOptions.files.electricalProject({
                                                            ElectricalProjectFileId: file.ElectricalProjectFileId!,
                                                            ImageQuality: 'OriginalWebp',
                                                        })}
                                                        enabled={!!file.ElectricalProjectFileId}
                                                    />
                                                )}
                                            </CarouselItem>
                                        ))}
                                    </CarouselContent>
                                </Carousel>
                            )
                        : (
                                <div className="flex items-center justify-center h-52 text-text-primary/50 rounded-lg border-2 border-dashed">
                                    <p>{t('module-electrical.pv-installations.messages.noImagesFound')}</p>
                                </div>
                            )}
                </div>
            </div>

            <div className="w-full h-px block bg-border-primary"></div>

            <div className="grid grid-cols-1 gap-0 sm:grid-cols-2 sm:gap-2 md:grid-cols-3 md:gap-4">
                <div className="col-span-1 text-sm mb-4 md:mb-0">
                    <div className="font-semibold ">
                        {t('module-electrical.pv-installations.technicalDetails.title')}
                    </div>
                    <div className="max-w-xs ">
                        {t('module-electrical.pv-installations.technicalDetails.subtitle')}
                    </div>
                </div>
                <div className="col-span-2">
                    <DataTable
                        columns={nameValueColumns}
                        data={[
                            {
                                Name: t('module-electrical.installation.technical-data.columns.brand'),
                                Value: projectQuery?.data?.PVInverter?.BrandModel
                                    ? projectQuery.data.PVInverter.BrandModel
                                    : t('module-electrical.pv-installations.messages.noBrand'),
                            },
                            {
                                Name: t('module-electrical.pv-installations.table.serialNumber'),
                                Value: projectQuery?.data?.PVInverter?.SerialNumber
                                    ? projectQuery.data.PVInverter.SerialNumber
                                    : t('module-electrical.pv-installations.messages.noSerialNumber'),
                            },
                            {
                                Name: t('module-electrical.pv-installations.table.firmwareVersion'),
                                Value: projectQuery?.data?.PVInverter?.FirmwareVersion
                                    ? projectQuery.data.PVInverter.FirmwareVersion
                                    : t('module-electrical.pv-installations.messages.noFirmwareVersion'),
                            },
                            {
                                Name: t('module-electrical.pv-installations.table.RatedPower'),
                                Value: projectQuery?.data?.PVInverter?.RatedPower
                                    ? `${projectQuery.data.PVInverter.RatedPower} kvA`
                                    : t('module-electrical.pv-installations.messages.noRatedPower'),
                            },
                        ]}
                        pageSize={10}
                    />
                </div>
            </div>

            <div className="w-full h-px block bg-border-primary"></div>

            <div className="grid grid-cols-1 gap-0 sm:grid-cols-2 sm:gap-2 md:grid-cols-3 md:gap-4">
                <div className="col-span-1 text-sm mb-4 md:mb-0">
                    <div className="font-semibold ">
                        {t('module-electrical.pv-installations.netimpedanties.title')}
                    </div>
                </div>
                <div className="col-span-2">
                    <DataTable
                        columns={nameValueColumns}
                        // (Ω)
                        data={[
                            {
                                Name: t('module-electrical.pv-installations.table.L1-PE'),
                                Value: projectQuery?.data?.PVInverter?.GroundImpedanceL1
                                    ? `${projectQuery.data.PVInverter.GroundImpedanceL1} (Ω)`
                                    : t('module-electrical.pv-installations.messages.noGroundImpedanceL1'),
                            },
                            {
                                Name: t('module-electrical.pv-installations.table.L2-PE'),
                                Value: projectQuery?.data?.PVInverter?.GroundImpedanceL2
                                    ? `${projectQuery.data.PVInverter.GroundImpedanceL2} (Ω)`
                                    : t('module-electrical.pv-installations.messages.noGroundImpedanceL2'),
                            },
                            {
                                Name: t('module-electrical.pv-installations.table.L3-PE'),
                                Value: projectQuery?.data?.PVInverter?.GroundImpedanceL3
                                    ? `${projectQuery.data.PVInverter.GroundImpedanceL3} (Ω)`
                                    : t('module-electrical.pv-installations.messages.noGroundImpedanceL3'),
                            },
                            {
                                Name: t('module-electrical.pv-installations.table.L1-L2'),
                                Value: projectQuery?.data?.PVInverter?.ImpedanceL1L2
                                    ? `${projectQuery.data.PVInverter.ImpedanceL1L2} (Ω)`
                                    : t('module-electrical.pv-installations.messages.noImpedanceL1L2'),
                            },
                            {
                                Name: t('module-electrical.pv-installations.table.L1-L3'),
                                Value: projectQuery?.data?.PVInverter?.ImpedanceL1L3
                                    ? `${projectQuery.data.PVInverter.ImpedanceL1L3} (Ω)`
                                    : t('module-electrical.pv-installations.messages.noImpedanceL1L3'),
                            },
                            {
                                Name: t('module-electrical.pv-installations.table.L2-L3'),
                                Value: projectQuery?.data?.PVInverter?.ImpedanceL2L3
                                    ? `${projectQuery.data.PVInverter.ImpedanceL2L3} (Ω)`
                                    : t('module-electrical.pv-installations.messages.noImpedanceL2L3'),
                            },
                            {
                                Name: t('module-electrical.pv-installations.table.L1-N'),
                                Value: projectQuery?.data?.PVInverter?.NeutralImpedanceL1
                                    ? `${projectQuery.data.PVInverter.NeutralImpedanceL1} (Ω)`
                                    : t('module-electrical.pv-installations.messages.noNeutralImpedanceL1'),
                            },
                            {
                                Name: t('module-electrical.pv-installations.table.L2-N'),
                                Value: projectQuery?.data?.PVInverter?.NeutralImpedanceL2
                                    ? `${projectQuery.data.PVInverter.NeutralImpedanceL2} (Ω)`
                                    : t('module-electrical.pv-installations.messages.noNeutralImpedanceL2'),
                            },
                            {
                                Name: t('module-electrical.pv-installations.table.L3-N'),
                                Value: projectQuery?.data?.PVInverter?.NeutralImpedanceL3
                                    ? `${projectQuery.data.PVInverter.NeutralImpedanceL3} (Ω)`
                                    : t('module-electrical.pv-installations.messages.noNeutralImpedanceL3'),
                            },
                        ]}
                        pageSize={10}
                    />
                </div>
            </div>

            <div className="w-full h-px block bg-border-primary"></div>

            <div className="grid grid-cols-1 gap-0 sm:grid-cols-2 sm:gap-2 md:grid-cols-3 md:gap-4">
                <div className="col-span-1 text-sm mb-4 md:mb-0">
                    <div className="font-semibold ">
                        {t('module-electrical.pv-installations.stringMeasurements.title')}
                    </div>
                </div>
                <div className="col-span-2">
                    {projectQuery?.data?.PVInverter?.Strings && projectQuery?.data?.PVInverter?.Strings.length > 0
                        ? (
                                <DataTable
                                    columns={stringMeasurementColumns}
                                    data={projectQuery?.data?.PVInverter?.Strings.filter(x => x.Identifier || x.Resistance || x.Voltage || x.Current) ?? []}
                                    pageSize={15}
                                />
                            )
                        : (
                                <div className="flex items-center justify-center h-52 text-text-primary/50 rounded-lg border-2 border-dashed">
                                    <p>{t('module-electrical.pv-installations.messages.noStringMeasurementsFound')}</p>
                                </div>
                            )}
                </div>
            </div>
            <div className="w-full h-px block bg-border-primary"></div>

            <div className="grid grid-cols-1 gap-0 sm:grid-cols-2 sm:gap-2 md:grid-cols-3 md:gap-4 mb-20">
                <div className="col-span-1 text-sm mb-4 md:mb-0">
                    <div className="font-semibold ">
                        {t('module-electrical.pv-installations.stringReferationTable.title')}
                    </div>
                </div>
                <div className="col-span-2">
                    {projectQuery?.data?.PVInverter?.Files && projectQuery?.data?.PVInverter?.Files.filter(row => row.Kind === 'PVStringTable').length > 0
                        ? (
                                <Carousel loop>
                                    <CarouselContent className="-ml-2 md:-ml-4">
                                        {projectQuery?.data?.PVInverter?.Files.filter(row => row.Kind === 'PVStringTable').map((file, idx) => (
                                            <CarouselItem className="pl-2 md:pl-4 basis-1/1 sm:basis-1/2 lg:basis-1/3 xl:basis-1/4" key={file.ElectricalProjectFileId ?? idx}>
                                                {file.File && (
                                                    <Image
                                                        className="h-52 w-auto object-contain rounded-lg"
                                                        alt={file?.File?.Name ?? ''}
                                                        queryOptions={portaalQueryOptions.files.electricalProject({
                                                            ElectricalProjectFileId: file.ElectricalProjectFileId!,
                                                            ImageQuality: 'OriginalWebp',
                                                        })}
                                                        enabled={!!file.ElectricalProjectFileId}
                                                    />
                                                )}
                                            </CarouselItem>
                                        ))}
                                    </CarouselContent>
                                </Carousel>
                            )
                        : (
                                <div className="flex items-center justify-center h-52 text-text-primary/50 rounded-lg border-2 border-dashed">
                                    <p>{t('module-electrical.pv-installations.messages.noImagesFound')}</p>
                                </div>
                            )}
                </div>
            </div>

        </div>
    );
}
